import React from 'react';

// Utils
import { SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT, SCHEMA_TYPE_YOUTUBE } from '../../util/types';
import {
  isFieldForCategory,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';

/**
 * Renders custom listing fields.
 * - SectionDetailsMaybe is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnumMaybe is used if schemaType is 'multi-enum'
 * - SectionTextMaybe is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  const propsForCustomFields =
    pickCustomFieldProps(
      publicData,
      metadata,
      listingFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  // Build a dynamic map of other_<fieldKey> free-text values from publicData
  const otherMap = {};
  Object.keys(publicData || {}).forEach(k => {
    if (k.startsWith('other_')) otherMap[k.slice(6)] = publicData[k];
  });
  const otherTextKeys = new Set(Object.keys(publicData || {}).filter(k => k.startsWith('other_')));

  // Replace 'other' in selectedOptions with the companion free-text value
  // and hide the standalone other_* text sections
  const resolvedPropsForCustomFields = propsForCustomFields
    .filter(f => !otherTextKeys.has(f.key))
    .map(fieldProps => {
      const freeText = otherMap[fieldProps.key];
      const COMPANION_VALS = ['other', 'us_state', 'australian_state'];
      if (
        freeText &&
        Array.isArray(fieldProps.selectedOptions) &&
        fieldProps.selectedOptions.some(v => COMPANION_VALS.includes(v))
      ) {
        return {
          ...fieldProps,
          options: [
            ...fieldProps.options.filter(o => !COMPANION_VALS.includes(o.key)),
            { key: freeText, label: freeText },
          ],
          selectedOptions: fieldProps.selectedOptions.map(v => (COMPANION_VALS.includes(v) ? freeText : v)),
        };
      }
      return fieldProps;
    });

  return (
    <>
      <SectionDetailsMaybe {...props} isFieldForCategory={isFieldForSelectedCategories} />
      {resolvedPropsForCustomFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideoMaybe key={key} {...fieldProps} />
        ) : null;
      })}
    </>
  );
};

export default CustomListingFields;
